<script setup lang="ts">
import capitalize from 'lodash/capitalize';
import { computed, ref } from 'vue';

import QuantityStepper from '@/components/base/form/QuantityStepper.vue';
import ThemedButton from '@/components/base/ThemedButton.vue';
import { SkuIngredient, VolumetricIngredient } from '@/lib/customizers/ingredients';
import { gtag } from '@/utils/analytics';

const props = withDefaults(
  defineProps<{
    ingredient: VolumetricIngredient | SkuIngredient;
    quantityAdded?: number;
    mode: 'box' | 'mix';
    showPrice?: boolean;
  }>(),
  {
    quantityAdded: 0,
    showPrice: true,
  },
);

const emit = defineEmits(['add-ingredient', 'remove-ingredient']);

const quantity = computed({
  get: () => props.quantityAdded,
  set: (val) => {
    if (val > props.quantityAdded) {
      emit('add-ingredient', props.ingredient.productKey);
    } else if (val < props.quantityAdded) {
      emit('remove-ingredient', props.ingredient.productKey);
    }
  },
});

const quantityAddedText = computed(() => {
  const { quantityAdded, mode } = props;
  if (mode !== 'mix') return quantityAdded;

  return `${quantityAdded} Part${quantityAdded > 1 ? 's' : ''}`;
});

const isAdded = computed(() => props.quantityAdded > 0);
const showActions = ref(false);

const onToggleActions = () => {
  showActions.value = true;
};

const handleAddIngredient = () => {
  emit('add-ingredient', props.ingredient.productKey);
  gtag('event', `add_to_${props.mode}`, {
    ingredient_added: props.ingredient.name,
  });
  showActions.value = false;
};

const handleViewDetails = () => {
  gtag('event', 'view_ingredient_details', {
    mode: props.mode,
    ingredient_viewed: props.ingredient.name,
  });
};
</script>

<template>
  <div
    class="relative cursor-pointer"
    @mouseover="showActions = true"
    @mouseleave="showActions = false"
    @keydown.enter="handleAddIngredient"
    tabindex="0"
    :data-test="ingredient.name"
  >
    <div
      class="flex flex-col items-center justify-between w-full h-full p-2 bg-white rounded-lg"
      :class="showActions && 'opacity-30'"
      data-test="ingredient-quantity"
      @click="onToggleActions"
    >
      <div class="flex items-center prop-3x2">
        <img :src="ingredient.listingImageUrl" class="max-w-full" alt="product-image" />
      </div>
      <div class="mt-2 text-sm font-semibold whitespace-normal">{{ ingredient.name }}</div>
      <div
        v-if="showPrice && !isAdded && 'priceRange' in ingredient"
        class="absolute text-sm text-gray-400 font-sofia-pro top-2 right-4"
      >
        {{ ingredient.priceRange }}
      </div>
      <div
        v-if="isAdded"
        class="absolute top-0 right-0 px-2 py-1 text-xs font-semibold rounded-tr-lg rounded-bl-lg bg-nuts-amber-400"
        data-test="quantity-added"
      >
        {{ quantityAddedText }} Added
      </div>
    </div>
    <div
      v-if="showActions"
      class="absolute top-0 bottom-0 left-0 right-0 flex flex-col items-center justify-center w-full h-full gap-4 p-4"
    >
      <div v-if="quantityAdded >= 1" class="flex flex-col items-center justify-center gap-2">
        <QuantityStepper
          v-model="quantity"
          :min="0"
          class="bg-white"
          ariaLabel="ingredient quantity"
        >
          {{ quantityAddedText }}
        </QuantityStepper>
      </div>
      <template v-else>
        <ThemedButton
          theme="gray"
          fullWidth
          @click="handleAddIngredient"
          :data-test="`add-to-${mode}-button`"
        >
          {{ `Add to ${capitalize(mode)}` }}
        </ThemedButton>
        <RouterLink
          :to="ingredient.path"
          class="w-full hover:no-underline"
          target="_blank"
          @click="handleViewDetails"
        >
          <ThemedButton class="hidden lg:inline" theme="white" fullWidth tabindex="-1">
            View Details
            <img src="@/assets/external-link.svg" class="ml-2" alt="link" />
          </ThemedButton>
        </RouterLink>
      </template>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.hidden {
  visibility: visible;
}
</style>
